import { useCallback, useEffect, useState } from "react";
import { Wallet, RefreshCw, BellRing, AlertTriangle, CheckCircle2, Activity, ShieldCheck, Webhook, ScrollText, Loader2, FileDown, FlaskConical, BookOpen, Trash2, ExternalLink, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Admin-side: sanitert Vipps-logg, webhook-helse per arrangør, siste mottekne varsel og oppsett av Vipps-testmiljøet. Berre bokmål (admin, som dei andre admin-sidene).

const GUIDE_PDF = "/docs/vipps-oppsett-steg-for-steg.pdf";
const EMPTY_TEST_FORM = { client_id: "", client_secret: "", subscription_key: "", msn: "", test_phone: "" };

const TEST_STEPS = [
  { title: "Skaff testbruker og testnøkler hos Vipps", text: "Enklest: Vipps sitt testskjema for partnere (vippsmobilepay.com/en-NO/partner-test-form) – du får test-salgsenhet, nøkler og testbruker på e-post. Alternativt portal.vippsmobilepay.com → For developers (Test users / API keys → Test environment)." },
  { title: "Installer Vipps MT-testappen på telefonen", text: "iOS: TestFlight-lenken i veiledningen. Android: bli med i Google-gruppen, så Play Store. Logg inn med testbrukerens fødselsnummer og telefonnummer (ikke ditt eget)." },
  { title: "Lim inn testnøklene og testbrukerens nummer her", text: "Nøklene sjekkes direkte mot Vipps sitt testmiljø før de lagres. Testarrangementet (10 kr) tar imot testbetalinger med en gang." },
  { title: "Godkjenn én testbetaling manuelt i MT-appen", text: "Kjøp en billett på testarrangementet med MT-appen. Vipps krever én manuell godkjenning før den automatiske testmatrisen kan godkjenne betalinger for deg." },
  { title: "Be om at testmatrisen kjøres", text: "Åtte sjekklistepunkter kjøres mot Vipps sitt testmiljø, rapporten og partnersøknaden (PDF) oppdateres automatisk." },
  { title: "Publiser til rightin.no og registrer webhooks", text: "Etter Republish: åpne denne siden på rightin.no og trykk «Registrer webhooks for alle arrangører». Send så partnersøknaden til Vipps." },
];

function VippsTestSetupCard({ onChanged }) {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [form, setForm] = useState(EMPTY_TEST_FORM);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/vipps/test-setup");
      setSetup(res.data);
      setForm((f) => ({ ...f, msn: f.msn || res.data.msn || "", test_phone: f.test_phone || res.data.test_phone || "" }));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const canSave = form.client_id.trim().length >= 8 && form.client_secret.trim().length >= 8 && form.subscription_key.trim().length >= 8 && form.msn.trim().length >= 4;

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await api.post("/admin/vipps/test-setup", {
        client_id: form.client_id.trim(), client_secret: form.client_secret.trim(), subscription_key: form.subscription_key.trim(), msn: form.msn.trim(), test_phone: form.test_phone.trim(),
      });
      toast.success(res.data.message);
      setSetup(res.data.setup);
      setForm({ ...EMPTY_TEST_FORM, msn: res.data.setup?.msn || "", test_phone: res.data.setup?.test_phone || "" });
      setShowForm(false);
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      const res = await api.delete("/admin/vipps/test-setup");
      toast.success(res.data.message);
      setSetup(res.data.setup);
      setForm(EMPTY_TEST_FORM);
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRemoving(false);
    }
  };

  const run = setup?.last_matrix_run;

  return (
    <Card className="border-border" data-testid="admin-vipps-test-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-heading font-semibold flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Testmiljø (Vipps MT)</p>
            <p className="text-sm text-muted-foreground mt-1">
              Her legger du inn Vipps sine <b>testnøkler</b> for testarrangøren, så slipper du å logge inn som den. Ingen ekte penger flyttes i testmiljøet. Nøklene vises aldri igjen etter lagring.
            </p>
          </div>
          <Button asChild variant="outline" data-testid="admin-vipps-guide-link">
            <a href={GUIDE_PDF} target="_blank" rel="noreferrer">
              <BookOpen className="w-4 h-4 mr-1.5" /> Veiledning steg for steg (PDF)
            </a>
          </Button>
        </div>

        {loading || !setup ? (
          <Skeleton className="h-20 w-full rounded-xl" data-testid="admin-vipps-test-loading" />
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-[1.1fr_1fr]">
              <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm space-y-2" data-testid="admin-vipps-test-status">
                <div className="flex items-center gap-2">
                  {setup.configured ? (
                    <Badge className="bg-[hsl(var(--success))]/15 text-foreground border border-[hsl(var(--success))]/50 hover:bg-[hsl(var(--success))]/15 font-normal" data-testid="admin-vipps-test-configured-badge">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Testnøkler lagret
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="font-normal text-muted-foreground" data-testid="admin-vipps-test-configured-badge">Ikke satt opp ennå</Badge>
                  )}
                  {setup.configured && <WebhookBadge status={setup.webhook.status} testId="admin-vipps-test-webhook-badge" />}
                </div>
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-muted-foreground">
                  <span>Testarrangør</span><span className="text-foreground">{setup.organizer_email}</span>
                  <span>Testarrangement</span>
                  <span className="text-foreground">
                    {setup.fixture_exists ? (
                      <a href={setup.event_url} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline inline-flex items-center gap-1" data-testid="admin-vipps-test-event-link">
                        {setup.event_title} ({setup.event_price} kr) <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground" data-testid="admin-vipps-test-event-missing">opprettes automatisk når du lagrer testnøklene ({setup.event_price} kr, skjult fra forsiden)</span>
                    )}
                  </span>
                  <span>MSN (test)</span><span className="text-foreground font-mono" data-testid="admin-vipps-test-msn">{setup.msn || "–"}</span>
                  <span>client_id</span><span className="text-foreground font-mono" data-testid="admin-vipps-test-client-id">{setup.client_id_masked || "–"}</span>
                  <span>Testbruker</span>
                  <span className="text-foreground font-mono inline-flex items-center gap-1.5" data-testid="admin-vipps-test-phone">
                    <Smartphone className="w-3.5 h-3.5 text-muted-foreground" />{setup.test_phone || "ikke lagt inn"}
                  </span>
                  <span>Sist lagret</span><span className="text-foreground">{setup.updated_at ? formatDateTime(setup.updated_at) : "–"}</span>
                </div>
                {setup.configured && setup.webhook.status === "error" && setup.webhook.error && (
                  <p className="text-xs text-[hsl(var(--warning))]">{setup.webhook.error}</p>
                )}
                {!setup.webhook_url_usable && (
                  <p className="text-xs text-[hsl(var(--warning))]" data-testid="admin-vipps-test-webhook-warning">Webhook-adressen i dette miljøet er ikke offentlig – varsler fra Vipps vil ikke nå fram her.</p>
                )}
              </div>

              <div className="rounded-xl border border-border p-4 text-sm space-y-2" data-testid="admin-vipps-test-matrix">
                <p className="font-medium flex items-center gap-2"><Activity className="w-4 h-4" /> Testmatrise (8 punkter)</p>
                {run ? (
                  <>
                    <p className="text-muted-foreground text-xs">{run.heading}</p>
                    <p data-testid="admin-vipps-test-matrix-summary">
                      <span className="text-[hsl(var(--success))] font-semibold">{run.passed} bestått</span>
                      {run.failed > 0 && <span className="text-[hsl(var(--warning))] font-semibold"> · {run.failed} feilet</span>}
                      <span className="text-muted-foreground"> · {run.items.length} punkter</span>
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      {run.items.map((i) => (
                        <li key={`${i.item}-${i.title}`}>{i.item}. {i.title}: <span className={i.status === "PASS" ? "text-[hsl(var(--success))]" : i.status === "FAIL" ? "text-[hsl(var(--warning))]" : ""}>{i.status}</span></li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-muted-foreground" data-testid="admin-vipps-test-matrix-empty">
                    Ikke kjørt ennå. Når nøklene er lagret og du har godkjent én testbetaling manuelt i MT-appen, ber du om at testmatrisen kjøres – resultatet vises her og i partnersøknaden.
                  </p>
                )}
              </div>
            </div>

            <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-vipps-test-steps">
              {TEST_STEPS.map((s, i) => (
                <li key={s.title} className="rounded-lg border border-border p-3 text-sm flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold inline-flex items-center justify-center">{i + 1}</span>
                  <span>
                    <span className="font-medium block">{s.title}</span>
                    <span className="text-xs text-muted-foreground">{s.text}</span>
                  </span>
                </li>
              ))}
            </ol>

            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" variant={showForm ? "outline" : "default"} onClick={() => setShowForm((v) => !v)} data-testid="admin-vipps-test-toggle-form-button">
                <FlaskConical className="w-4 h-4 mr-1.5" /> {showForm ? "Skjul skjemaet" : setup.configured ? "Bytt testnøkler" : "Legg inn testnøkler"}
              </Button>
              {setup.configured && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="ghost" className="text-muted-foreground" disabled={removing} data-testid="admin-vipps-test-remove-button">
                      {removing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />} Fjern testoppsettet
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Fjerne testnøklene?</AlertDialogTitle>
                      <AlertDialogDescription>Testarrangementet slutter å ta imot Vipps-testbetalinger til du legger inn nøkler igjen. Ingen ekte arrangører påvirkes.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="admin-vipps-test-remove-cancel">Avbryt</AlertDialogCancel>
                      <AlertDialogAction onClick={remove} data-testid="admin-vipps-test-remove-confirm">Ja, fjern</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>

            {showForm && (
              <form onSubmit={save} className="rounded-xl border border-border p-4 space-y-4" data-testid="admin-vipps-test-form">
                <p className="text-sm text-muted-foreground">
                  Alt dette finner du i <b>portal.vippsmobilepay.com → For developers</b>. Pass på at du står i <b>Test environment</b> – produksjonsnøkler blir avvist her.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="vt-msn">MSN (Merchant Serial Number) for test-salgsenheten</Label>
                    <Input id="vt-msn" value={form.msn} onChange={set("msn")} placeholder="f.eks. 123456" maxLength={20} data-testid="admin-vipps-test-msn-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="vt-phone">Testbrukerens telefonnummer (fra «Test users»)</Label>
                    <Input id="vt-phone" value={form.test_phone} onChange={set("test_phone")} placeholder="f.eks. 4712345678" maxLength={24} inputMode="numeric" data-testid="admin-vipps-test-phone-input" />
                    <p className="text-xs text-muted-foreground">Nummeret Vipps genererte til testbrukeren – ikke ditt eget nummer. Brukes av testmatrisen.</p>
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="vt-client-id">client_id</Label>
                    <Input id="vt-client-id" value={form.client_id} onChange={set("client_id")} autoComplete="off" data-testid="admin-vipps-test-client-id-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="vt-client-secret">client_secret</Label>
                    <Input id="vt-client-secret" type="password" value={form.client_secret} onChange={set("client_secret")} autoComplete="new-password" data-testid="admin-vipps-test-client-secret-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="vt-subscription-key">Ocp-Apim-Subscription-Key (primary)</Label>
                    <Input id="vt-subscription-key" type="password" value={form.subscription_key} onChange={set("subscription_key")} autoComplete="new-password" data-testid="admin-vipps-test-subscription-key-input" />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button type="submit" disabled={!canSave || saving} data-testid="admin-vipps-test-save-button">
                    {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />}
                    {saving ? "Sjekker mot Vipps …" : "Lagre og verifiser mot testmiljøet"}
                  </Button>
                  <span className="text-xs text-muted-foreground">Miljø: <b>test</b> (apitest.vipps.no). Webhook registreres automatisk.</span>
                </div>
              </form>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

const OP_LABELS = {
  access_token: "Token",
  create_payment: "Opprett betaling",
  get_payment: "Hent status",
  capture_payment: "Capture",
  cancel_payment: "Kanseller",
  refund_payment: "Refusjon",
  payment_events: "Hendelseslogg",
  list_webhooks: "List webhooks",
  register_webhook: "Registrer webhook",
  delete_webhook: "Slett webhook",
  webhook_in: "Webhook inn",
};

const WEBHOOK_LABEL = {
  active: "Aktiv",
  error: "Feilet",
  skipped: "Ikke tilgjengelig",
  missing: "Ikke registrert",
};

const kr = (ore) => (ore === null || ore === undefined ? "–" : `${Math.round(ore / 100)} kr`);
const shortRef = (ref) => (ref && ref.length > 14 ? `${ref.slice(0, 8)}…${ref.slice(-4)}` : ref || "–");

function WebhookBadge({ status, testId }) {
  if (status === "active") {
    return (
      <Badge className="bg-[hsl(var(--success))]/15 text-foreground border border-[hsl(var(--success))]/50 hover:bg-[hsl(var(--success))]/15 font-normal" data-testid={testId}>
        <BellRing className="w-3 h-3 mr-1" />{WEBHOOK_LABEL.active}
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge className="bg-[hsl(var(--warning))]/20 text-foreground border border-[hsl(var(--warning))]/60 hover:bg-[hsl(var(--warning))]/20 font-normal" data-testid={testId}>
        <AlertTriangle className="w-3 h-3 mr-1" />{WEBHOOK_LABEL.error}
      </Badge>
    );
  }
  return <Badge variant="outline" className="font-normal text-muted-foreground" data-testid={testId}>{WEBHOOK_LABEL[status] || status}</Badge>;
}

export default function AdminVippsPage() {
  const { organizer } = useAuth();
  const [overview, setOverview] = useState(null);
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(false);
  const [msnFilter, setMsnFilter] = useState("");
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [limit, setLimit] = useState("100");
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const res = await api.get("/admin/vipps/overview");
      setOverview(res.data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const res = await api.get("/admin/vipps/log", { params: { limit: Number(limit), msn: msnFilter.trim() || undefined, only_errors: onlyErrors || undefined } });
      setLog(res.data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLogLoading(false);
    }
  }, [limit, msnFilter, onlyErrors]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    const timer = setTimeout(loadLog, 250);
    return () => clearTimeout(timer);
  }, [loadLog]);

  const registerAllWebhooks = async () => {
    setRegistering(true);
    try {
      const res = await api.post("/admin/vipps/register-webhooks");
      setRegisterResult(res.data);
      toast.success(`Webhooks: ${res.data.registered} registrert, ${res.data.already_active} allerede aktive, ${res.data.errors} feil`);
      loadOverview();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRegistering(false);
    }
  };

  const refreshAll = () => {
    setLoading(true);
    loadOverview();
    loadLog();
  };

  if (organizer && !organizer.is_admin) {
    return (
      <DashboardLayout>
        <p className="text-muted-foreground" data-testid="admin-vipps-denied">Denne siden krever plattform-administrator.</p>
      </DashboardLayout>
    );
  }

  const sys = overview?.system;
  const stats = overview?.stats_24h;

  return (
    <DashboardLayout>
      <div className="max-w-6xl space-y-6" data-testid="admin-vipps-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
              <Wallet className="w-7 h-7" /> Vipps (admin)
            </h1>
            <p className="text-muted-foreground mt-1">
              Sanitert operasjonslogg (aldri nøkler eller tokens), webhook-helse per arrangør og siste varsler fra Vipps. Hver arrangør er sin egen Vipps-merchant – Right in håndterer aldri pengene.
            </p>
          </div>
          <Button variant="outline" onClick={refreshAll} disabled={loading || logLoading} data-testid="admin-vipps-refresh-button">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading || logLoading ? "animate-spin" : ""}`} />
            Oppdater
          </Button>
        </div>

        {loading || !overview ? (
          <div className="space-y-3" data-testid="admin-vipps-loading">
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        ) : (
          <>
            {/* System + 24 t */}
            <div className="grid gap-4 lg:grid-cols-[1.2fr_2fr]">
              <Card className="border-border" data-testid="admin-vipps-system-card">
                <CardContent className="p-5 space-y-2 text-sm">
                  <p className="font-heading font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Integrasjon</p>
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-muted-foreground">
                    <span>System</span><span className="text-foreground font-mono text-xs" data-testid="admin-vipps-system-name">{sys.name} {sys.version}</span>
                    <span>Plugin</span><span className="text-foreground font-mono text-xs" data-testid="admin-vipps-plugin">{sys.plugin} {sys.plugin_version}</span>
                    <span>Webhook-URL</span>
                    <span className="text-foreground font-mono text-xs break-all inline-flex items-start gap-1.5" data-testid="admin-vipps-webhook-url">
                      {sys.webhook_url_usable ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[hsl(var(--success))]" /> : <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[hsl(var(--warning))]" />}
                      <span>{sys.webhook_url || "(APP_BASE_URL mangler)"}</span>
                    </span>
                    <span>Auto-kansellering</span><span className="text-foreground">etter {sys.pending_timeout_min} min (hard grense {sys.hard_timeout_min} min)</span>
                    <span>Loggen beholdes</span><span className="text-foreground">{sys.log_ttl_days} dager</span>
                  </div>
                </CardContent>
              </Card>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-vipps-stats">
                {[
                  { label: "Vipps-kall siste 24 t", value: stats.calls, icon: Activity, testId: "admin-vipps-stat-calls" },
                  { label: "Feilede kall", value: stats.errors, icon: AlertTriangle, warn: stats.errors > 0, testId: "admin-vipps-stat-errors" },
                  { label: "Webhooks mottatt", value: stats.webhooks_received, icon: Webhook, testId: "admin-vipps-stat-webhooks" },
                  { label: "Webhooks avvist", value: stats.webhooks_rejected, icon: ShieldCheck, warn: stats.webhooks_rejected > 0, testId: "admin-vipps-stat-rejected" },
                ].map((s) => (
                  <Card key={s.testId} className="border-border" data-testid={s.testId}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between text-muted-foreground">
                        <span className="text-xs">{s.label}</span>
                        <s.icon className={`w-4 h-4 ${s.warn ? "text-[hsl(var(--warning))]" : ""}`} />
                      </div>
                      <p className={`text-2xl font-heading font-semibold tabular-nums mt-1 ${s.warn ? "text-[hsl(var(--warning))]" : ""}`}>{s.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Utrulling / partner-søknad */}
            <Card className="border-border" data-testid="admin-vipps-readiness-card">
              <CardContent className="p-5 space-y-3">
                <p className="font-heading font-semibold flex items-center gap-2"><Webhook className="w-4 h-4" /> Utrulling og partnersøknad</p>
                <p className="text-sm text-muted-foreground">
                  Arrangører som lagret Vipps-nøklene sine før varsling (webhooks) fantes, får ingen varsler før webhooken er registrert på salgsenheten deres.
                  Kjør dette én gang etter publisering til rightin.no – og når som helst senere, det hopper over dem som allerede er aktive.
                  {!sys.webhook_url_usable && <span className="block mt-1 text-[hsl(var(--warning))]">APP_BASE_URL er ikke en offentlig https-adresse i dette miljøet – registrering vil bli hoppet over.</span>}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={registerAllWebhooks} disabled={registering || !sys.webhook_url_usable} data-testid="admin-vipps-register-all-button">
                    {registering ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <BellRing className="w-4 h-4 mr-1.5" />}
                    Registrer webhooks for alle arrangører
                  </Button>
                  <Button asChild variant="outline" data-testid="admin-vipps-partner-pack-link">
                    <a href="/docs/vipps-partner-application-pack.pdf" target="_blank" rel="noreferrer">
                      <FileDown className="w-4 h-4 mr-1.5" /> Partnersøknad til Vipps (PDF)
                    </a>
                  </Button>
                  {registerResult && (
                    <span className="text-sm text-muted-foreground" data-testid="admin-vipps-register-all-result">
                      {registerResult.checked} sjekket · {registerResult.registered} registrert · {registerResult.already_active} allerede aktive · {registerResult.errors} feil · {registerResult.skipped} hoppet over
                    </span>
                  )}
                </div>
                {registerResult?.details?.length > 0 && (
                  <ul className="text-xs text-muted-foreground space-y-0.5" data-testid="admin-vipps-register-all-details">
                    {registerResult.details.map((d) => (
                      <li key={d.organizer_id}>
                        {d.org_name} (MSN {d.msn}): <span className={d.status === "active" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}>{WEBHOOK_LABEL[d.status] || d.status}</span>
                        {d.error ? ` – ${d.error}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Testmiljø (Vipps MT) */}
            <VippsTestSetupCard onChanged={loadOverview} />

            {/* Arrangører */}
            <Card className="border-border" data-testid="admin-vipps-organizers-card">
              <CardContent className="p-0">
                <div className="p-5 pb-3">
                  <p className="font-heading font-semibold flex items-center gap-2"><BellRing className="w-4 h-4" /> Arrangører med Vipps-oppsett ({overview.organizers.length})</p>
                  <p className="text-sm text-muted-foreground mt-1">Webhook-status, siste varsel og siste feil per salgsenhet (MSN). Arrangører med feil ligger øverst.</p>
                </div>
                {overview.organizers.length === 0 ? (
                  <p className="px-5 pb-5 text-sm text-muted-foreground" data-testid="admin-vipps-organizers-empty">Ingen arrangører har lagret Vipps-nøkler ennå.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-vipps-organizers-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Arrangør</TableHead>
                          <TableHead>MSN</TableHead>
                          <TableHead>Webhook</TableHead>
                          <TableHead>Siste varsel</TableHead>
                          <TableHead>Siste feil</TableHead>
                          <TableHead className="text-right">Betalt 24 t</TableHead>
                          <TableHead className="text-right">Venter nå</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {overview.organizers.map((o) => (
                          <TableRow key={o.organizer_id} data-testid={`admin-vipps-org-${o.organizer_id}`}>
                            <TableCell className="text-sm">
                              <p className="font-medium">{o.org_name}</p>
                              <p className="text-xs text-muted-foreground">{o.email}</p>
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">
                              <span className="font-mono">{o.msn}</span>
                              <Badge variant={o.env === "production" ? "default" : "secondary"} className="ml-2 font-normal">{o.env === "production" ? "Prod" : "Test"}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              <WebhookBadge status={o.webhook.status} testId={`admin-vipps-org-webhook-${o.organizer_id}`} />
                              {o.webhook.registered_at && <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">{formatDateTime(o.webhook.registered_at)}</p>}
                              {o.webhook.status === "error" && o.webhook.error && <p className="text-xs text-[hsl(var(--warning))] mt-1 max-w-[260px]">{o.webhook.error}</p>}
                            </TableCell>
                            <TableCell className="text-sm">
                              {o.last_webhook_event ? (
                                <>
                                  <p className="whitespace-nowrap">{o.last_webhook_event.name} <span className="text-xs text-muted-foreground">({o.last_webhook_event.result || "–"})</span></p>
                                  <p className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(o.last_webhook_event.received_at)}</p>
                                </>
                              ) : <span className="text-muted-foreground">–</span>}
                            </TableCell>
                            <TableCell className="text-sm">
                              {o.last_error ? (
                                <>
                                  <p className="whitespace-nowrap">{OP_LABELS[o.last_error.op] || o.last_error.op} · HTTP {o.last_error.status}</p>
                                  <p className="text-xs text-muted-foreground max-w-[260px] truncate" title={o.last_error.error?.detail || ""}>{o.last_error.error?.detail || o.last_error.error?.title || ""}</p>
                                  <p className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(o.last_error.at)}</p>
                                </>
                              ) : <span className="text-muted-foreground">–</span>}
                            </TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{o.paid_24h}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{o.pending_now}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Logg */}
            <Card className="border-border" data-testid="admin-vipps-log-card">
              <CardContent className="p-0">
                <div className="p-5 pb-3 flex flex-wrap items-end gap-4">
                  <div>
                    <p className="font-heading font-semibold flex items-center gap-2"><ScrollText className="w-4 h-4" /> Operasjonslogg</p>
                    <p className="text-sm text-muted-foreground mt-1">Hvert kall mot Vipps og hver innkommende webhook. Feil viser Vipps sin type/tittel/detalj og traceId – nøkler og tokens er utelatt.</p>
                  </div>
                  <div className="flex flex-wrap items-end gap-3 ml-auto">
                    <div className="space-y-1">
                      <Label htmlFor="vipps-log-msn" className="text-xs">MSN</Label>
                      <Input id="vipps-log-msn" value={msnFilter} onChange={(e) => setMsnFilter(e.target.value)} placeholder="Alle" className="h-9 w-32" data-testid="admin-vipps-log-msn-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Antall</Label>
                      <Select value={limit} onValueChange={setLimit}>
                        <SelectTrigger className="h-9 w-24" data-testid="admin-vipps-log-limit-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                          <SelectItem value="200">200</SelectItem>
                          <SelectItem value="500">500</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 h-9">
                      <Switch id="vipps-log-errors" checked={onlyErrors} onCheckedChange={setOnlyErrors} data-testid="admin-vipps-log-errors-switch" />
                      <Label htmlFor="vipps-log-errors" className="text-sm">Bare feil</Label>
                    </div>
                  </div>
                </div>
                {logLoading && !log ? (
                  <div className="p-5"><Skeleton className="h-24 w-full rounded-xl" /></div>
                ) : !log || log.entries.length === 0 ? (
                  <p className="px-5 pb-5 text-sm text-muted-foreground" data-testid="admin-vipps-log-empty">Ingen loggposter{onlyErrors ? " med feil" : ""}{msnFilter ? ` for MSN ${msnFilter}` : ""} de siste {sys.log_ttl_days} dagene.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-vipps-log-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tidspunkt</TableHead>
                          <TableHead>Operasjon</TableHead>
                          <TableHead>MSN</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">ms</TableHead>
                          <TableHead>Referanse</TableHead>
                          <TableHead>Feil / detalj</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {log.entries.map((e, i) => (
                          <TableRow key={`${e.at}-${i}`} className={e.ok ? undefined : "bg-[hsl(var(--warning))]/5"} data-testid={`admin-vipps-log-row-${i}`}>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(e.at)}</TableCell>
                            <TableCell className="text-sm whitespace-nowrap">
                              <span className="font-medium">{OP_LABELS[e.op] || e.op}</span>
                              <span className="block text-xs text-muted-foreground font-mono">{e.method} {e.path}</span>
                            </TableCell>
                            <TableCell className="text-sm font-mono">{e.msn || "–"}{e.env ? <span className="ml-1 text-xs text-muted-foreground font-sans">({e.env})</span> : null}</TableCell>
                            <TableCell className="text-sm whitespace-nowrap">
                              {e.ok ? (
                                <span className="inline-flex items-center gap-1 text-[hsl(var(--success))]"><CheckCircle2 className="w-3.5 h-3.5" />{e.status}</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[hsl(var(--warning))]"><AlertTriangle className="w-3.5 h-3.5" />{e.status || "nett"}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-right tabular-nums text-muted-foreground">{e.ms ?? "–"}</TableCell>
                            <TableCell className="text-xs font-mono" title={e.context?.reference || ""}>{shortRef(e.context?.reference)}{e.context?.name ? <span className="font-sans text-muted-foreground"> · {e.context.name}</span> : null}{e.context?.result ? <span className="font-sans text-muted-foreground"> → {e.context.result}</span> : null}</TableCell>
                            <TableCell className="text-xs max-w-[320px]">
                              {e.error ? (
                                <>
                                  <p className="font-medium truncate" title={e.error.title}>{e.error.title || "Feil"}</p>
                                  <p className="text-muted-foreground line-clamp-2" title={e.error.detail}>{e.error.detail}</p>
                                  {e.error.trace_id && <p className="font-mono text-muted-foreground">traceId {e.error.trace_id}</p>}
                                </>
                              ) : <span className="text-muted-foreground">–</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <p className="px-5 py-3 text-xs text-muted-foreground border-t border-border" data-testid="admin-vipps-log-count">
                      {log.count} poster vist · {log.webhook_events_total} webhook-hendelser lagret totalt
                      {logLoading && <Loader2 className="inline w-3 h-3 ml-2 animate-spin" />}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Webhook-hendelser */}
            <Card className="border-border" data-testid="admin-vipps-events-card">
              <CardContent className="p-0">
                <div className="p-5 pb-3">
                  <p className="font-heading font-semibold flex items-center gap-2"><Webhook className="w-4 h-4" /> Siste webhook-hendelser ({overview.recent_webhook_events.length})</p>
                  <p className="text-sm text-muted-foreground mt-1">Varsler fra Vipps som er verifisert og behandlet (duplikater er allerede filtrert bort).</p>
                </div>
                {overview.recent_webhook_events.length === 0 ? (
                  <p className="px-5 pb-5 text-sm text-muted-foreground" data-testid="admin-vipps-events-empty">Ingen webhooks mottatt ennå.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-vipps-events-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Mottatt</TableHead>
                          <TableHead>MSN</TableHead>
                          <TableHead>Hendelse</TableHead>
                          <TableHead>Ordre</TableHead>
                          <TableHead className="text-right">Beløp</TableHead>
                          <TableHead>Resultat</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {overview.recent_webhook_events.map((ev) => (
                          <TableRow key={ev.event_key} data-testid={`admin-vipps-event-${ev.event_key}`}>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(ev.received_at)}</TableCell>
                            <TableCell className="text-sm font-mono">{ev.msn}</TableCell>
                            <TableCell className="text-sm">{ev.name}{ev.success === false && <AlertTriangle className="inline w-3.5 h-3.5 ml-1 text-[hsl(var(--warning))]" />}</TableCell>
                            <TableCell className="text-xs font-mono" title={ev.reference}>{shortRef(ev.reference)}</TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{kr(ev.amount_ore)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{ev.result || (ev.processed_at ? "behandlet" : "venter")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
